import get from 'lodash/fp/get';
import merge from 'lodash/fp/merge';
import { LOCATION_CHANGE } from 'connected-react-router';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { actionReceiveSecondaryContent } from 'actions';
import { RECEIVE_PRIMARY_CONTENT } from 'config/types/action';
import relationMap from 'config/types/content/relations';
import { contentItemPathTypeMap } from 'config/types/path';
import getMatch from 'selectors/getMatch';
import { createGetContentItemBySlug } from 'selectors/createGetContentItem';
import hasFetchedActiveContent from 'selectors/hasFetchedActiveContent';

export default function* contentItemMetaSaga(service) {
  yield takeLatest(LOCATION_CHANGE, watchForContentItem, service);
}

/**
 * Emit side effects related to page metadata.
 * @param {function} fetchContentItemsByIds
 */
export function* watchForContentItem({ fetchContentItemsByIds }) {
  const match = yield select(getMatch);
  const { params, pathType } = match;

  const isContentMatch = Object
    .keys(contentItemPathTypeMap)
    .includes(pathType);
  if (!isContentMatch) {
    return;
  }

  if (!(yield select(hasFetchedActiveContent))) {
    yield take(RECEIVE_PRIMARY_CONTENT);
  }

  const contentType = contentItemPathTypeMap[pathType] || params.contentType;
  const getItem = createGetContentItemBySlug(contentType);
  const item = yield select((state) => getItem(state, { match }));
  if (!item) {
    return;
  }

  const relations = relationMap[contentType] || relationMap[pathType];
  if (!relations) {
    return;
  }

  const isLazyLoaded = (path) => !relations[path].eager;
  const effects = Object
    .keys(relations)
    .filter(isLazyLoaded)
    .map((path) => {
      const relation = relations[path];
      const ids = get(`metadata.${path}`, item);
      return call(fetchContentItemsByIds, ids, relation.contentType);
    });

  if (!effects.length) {
    return;
  }

  const results = yield all(effects);
  const mergeContent = (acc, result) => merge(acc, result);
  const content = results.reduce(mergeContent, {});
  yield put(actionReceiveSecondaryContent(content));
}
